import React from 'react';
import Cookies from 'js-cookie';
import ReCAPTCHA from 'react-google-recaptcha';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Button, Card, Container, Flex, Heading, Skeleton, Text, TextField } from '@radix-ui/themes';
import { toast } from 'sonner';
import NavBar from 'Components/element/NavBar';
import Footer from 'Components/element/Footer';
import { FormField } from 'shared/components';
import { CheckCircleOutlineIcon, VisibilityIcon, VisibilityOffIcon } from 'shared/icons';
import { VerifyAccountDialog } from 'feature-auth/verify-account-dialog';
import { useCandidateLogin } from './candidate-login.provider';

type CandidateLoginViewProps = {
   captchaKey: string | null;
};

function CandidateLoginView({ captchaKey }: CandidateLoginViewProps) {
   const { t } = useTranslation('global');
   const navigate = useNavigate();
   const store = useCandidateLogin();

   const currentLanguage = Cookies.get('selectedLanguage') || 'ukr';
   const recaptchaLanguage = currentLanguage === 'ukr' ? 'uk' : currentLanguage;
   const authToastOptions = { classNames: { toast: 'auth-session-toast' } };
   const authSuccessToastOptions = {
      ...authToastOptions,
      icon: <CheckCircleOutlineIcon style={{ color: '#93c47d', width: 24, height: 24 }} />,
   };
   const [captchaReady, setCaptchaReady] = React.useState(false);

   React.useEffect(() => {
      window.scrollTo(0, 0);

      if (store.hasActiveSession) {
         navigate('/');
         toast.warning(t('jobseekerLogin.alreadyLoggedIn'), authToastOptions);
      }
   }, [navigate, store.hasActiveSession, t]);

   const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      await store.submit({
         onSuccess: (_resp, user) => {
            toast.success(t('jobseekerLogin.welcome') + ', ' + user.first_name, authSuccessToastOptions);
            navigate('/candidate/my-cv-list');
         },
         onVerificationRequired: (response) => {
            const loginValue = store.form.controls.login.value.trim();
            const email = response.response?.email_address || (!response.sms ? loginValue : '');
            const contact = response.response?.contact || (response.sms ? loginValue : '');

            store.verifyAccountDialogStore.openDialog({
               message: response.message,
               email,
               contact,
               link: email ? `mailto:${email}` : '',
            });
         },
         onError: (message) => {
            toast.error(message, authToastOptions);
         },
      });
   };

   return (
      <>
         <NavBar />
         <Container size="4" py="9">
            <Card size="2">
               <Flex direction={{ initial: 'column', md: 'row' }} gap="0">
                  <Box width={{ initial: '100%', md: '49%' }} style={{ alignSelf: 'flex-start' }}>
                     <img
                        src="/Images/jobseekerLogin.jpg"
                        alt="candidate-login"
                        style={{
                           width: '100%',
                           height: 'clamp(320px, 36vw, 560px)',
                           borderRadius: '10px',
                           objectFit: 'cover',
                        }}
                     />
                  </Box>

                  <Box width={{ initial: '100%', md: '51%' }} px={{ initial: '2', md: '3' }} py={{ initial: '3', md: '4' }}>
                     <Flex direction="column" gap="3">
                        <Flex justify="center">
                           <img src="/Images/Logo.png" alt="logo" width="56" />
                        </Flex>

                        <Heading align="center" size="6" weight="medium">
                           {t('jobseekerLogin.jobseekerLogin')}
                        </Heading>

                        <form onSubmit={onSubmit} style={{ maxWidth: 440, width: '100%', margin: '0 auto' }}>
                           <Flex direction="column" gap="4">
                              <FormField
                                 label={'Логін'}
                                 error={store.form.controls.login.error}
                                 required
                              >
                                 <TextField.Root
                                    size="3"
                                    value={store.form.controls.login.value}
                                    placeholder={'Електронна пошта або телефон'}
                                    onChange={(e) => {
                                       store.form.controls.login.setValue(e.target.value);
                                    }}
                                 ></TextField.Root>
                              </FormField>

                              <FormField
                                 label={t('jobseekerLogin.password')}
                                 error={store.form.controls.password.error}
                                 required
                              >
                                 <TextField.Root
                                    size="3"
                                    type={store.showPassword ? 'text' : 'password'}
                                    value={store.form.controls.password.value}
                                    placeholder={t('jobseekerLogin.password')}
                                    onChange={(e) => {
                                       store.form.controls.password.setValue(e.target.value);
                                    }}
                                 >
                                    <TextField.Slot side="right">
                                       <Button
                                          type="button"
                                          size="1"
                                          variant="ghost"
                                          color="gray"
                                          aria-label={store.showPassword ? 'Hide password' : 'Show password'}
                                          onClick={store.togglePasswordVisibility}
                                       >
                                          {store.showPassword ? (
                                             <VisibilityOffIcon style={{ width: 18, height: 18 }} />
                                          ) : (
                                             <VisibilityIcon style={{ width: 18, height: 18 }} />
                                          )}
                                       </Button>
                                    </TextField.Slot>
                                 </TextField.Root>
                              </FormField>

                              <Box>
                                 <Box style={{ width: 304, minHeight: 78, position: 'relative' }}>
                                    {!captchaReady && <Skeleton width="304px" height="78px" />}

                                    {captchaKey && (
                                       <Box
                                          style={{
                                             position: captchaReady ? 'static' : 'absolute',
                                             inset: 0,
                                             opacity: captchaReady ? 1 : 0,
                                             pointerEvents: captchaReady ? 'auto' : 'none',
                                          }}
                                       >
                                          <ReCAPTCHA
                                             sitekey={captchaKey}
                                             hl={recaptchaLanguage}
                                             asyncScriptOnLoad={() => setCaptchaReady(true)}
                                             onErrored={() => setCaptchaReady(false)}
                                             onChange={(value) => store.setCaptchaToken(value)}
                                          />
                                       </Box>
                                    )}
                                 </Box>
                                 {store.captchaError && (
                                    <Text size="2" color="red">
                                       {store.captchaError}
                                    </Text>
                                 )}
                              </Box>

                              <Flex justify="between" align="center" wrap="wrap" gap="3">
                                 <Link to="/users/forgotpassword" state={{ user: 'candidate' }}>
                                    {t('jobseekerLogin.forgotPassword')}
                                 </Link>

                                 <Button size="3" type="submit" loading={store.submitting}>
                                    {t('jobseekerLogin.login')}
                                 </Button>
                              </Flex>

                              <Text size="2">
                                 {t('jobseekerLogin.bottomTxt3')}{' '}
                                 <Link to="/auth/candidate-register">
                                    {t('jobseekerLogin.bottomTxt4')}
                                 </Link>
                              </Text>
                           </Flex>
                        </form>
                     </Flex>
                  </Box>
               </Flex>
            </Card>
         </Container>
         <VerifyAccountDialog
            store={store.verifyAccountDialogStore}
            title={"Підтвердження акаунту"}
            cancelText={"Закрити"}
            continueText={"Підтвердити"}
            onActivated={() => {
               toast.success(
                  t('jobseekerLogin.welcome') + ', ' + (store.rootStore.userStore.user?.first_name ?? ''),
                  authSuccessToastOptions
               );
               navigate('/candidate/my-cv-list');
            }}
         />
         <Footer />
      </>
   );
}

export const CandidateLogin = observer(CandidateLoginView);
